import { useCfaSwitcher } from '@/core/cfa/useCfaSwitcher';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { cn } from '@/shared/lib/utils';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  supervisor: 'Sup.',
  operator: 'Op.',
};

// Distinct vivid color palettes per CFA — deterministic via hash
const CFA_COLORS = [
  { // emerald
    pill: 'bg-emerald-100 text-emerald-800 border-emerald-400 hover:bg-emerald-200 hover:border-emerald-500 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-600 dark:hover:bg-emerald-900/60',
    dot: 'bg-emerald-500',
  },
  { // blue
    pill: 'bg-blue-100 text-blue-800 border-blue-400 hover:bg-blue-200 hover:border-blue-500 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-600 dark:hover:bg-blue-900/60',
    dot: 'bg-blue-500',
  },
  { // amber
    pill: 'bg-amber-100 text-amber-800 border-amber-400 hover:bg-amber-200 hover:border-amber-500 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-600 dark:hover:bg-amber-900/60',
    dot: 'bg-amber-500',
  },
  { // violet
    pill: 'bg-violet-100 text-violet-800 border-violet-400 hover:bg-violet-200 hover:border-violet-500 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-600 dark:hover:bg-violet-900/60',
    dot: 'bg-violet-500',
  },
  { // rose
    pill: 'bg-rose-100 text-rose-800 border-rose-400 hover:bg-rose-200 hover:border-rose-500 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-600 dark:hover:bg-rose-900/60',
    dot: 'bg-rose-500',
  },
] as const;

function getCfaColorIndex(cfaId: string): number {
  let hash = 0;
  for (let i = 0; i < cfaId.length; i++) {
    hash = ((hash << 5) - hash + cfaId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % CFA_COLORS.length;
}

interface CfaSwitcherProps {
  className?: string;
}

/**
 * CFA Switcher shown in the header.
 * - Single CFA: shows name as plain text (no dropdown).
 * - Multiple CFAs: shows name with dropdown to switch.
 */
export function CfaSwitcher({ className }: CfaSwitcherProps) {
  const { cfaName, assignments, isMultiCfa, currentCfaId, switchCfa } = useCfaSwitcher();

  if (!cfaName) return null;

  // Single CFA — plain text, no interactivity
  if (!isMultiCfa) {
    return (
      <span className={className || 'text-xl font-bold text-primary'}>
        {cfaName}
      </span>
    );
  }

  // Multiple CFAs — per-CFA colored pill with dropdown
  const currentColor = CFA_COLORS[getCfaColorIndex(currentCfaId!)];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={cn(
        'flex items-center gap-1.5 outline-none transition-all',
        'border-2 rounded-full px-4 py-1.5',
        'text-lg font-bold shadow-sm',
        currentColor.pill,
        className,
      )}>
        {cfaName}
        <ChevronDown className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[220px]">
        {assignments.map((a) => {
          const color = CFA_COLORS[getCfaColorIndex(a.cfa_id)];
          return (
            <DropdownMenuItem
              key={a.cfa_id}
              onClick={() => switchCfa(a.cfa_id)}
              className={a.cfa_id === currentCfaId ? 'bg-accent font-semibold' : ''}
            >
              <div className="flex items-center gap-2 w-full">
                <span className={cn('w-2.5 h-2.5 rounded-full shrink-0', color.dot)} />
                <span className="flex-1">{a.cfa_nome}</span>
                <span className="text-xs text-muted-foreground ml-3">
                  {ROLE_LABELS[a.role] || a.role}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
